import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { CookieService, PlatformService, TimeService } from 'ngx-unificator/services';
import { useStore } from 'ngx-unificator/store';
import { combineLatest, from, Observable, of } from 'rxjs';
import { delay, distinctUntilChanged, filter, map, switchMap, takeWhile, tap } from 'rxjs/operators';
import { OnlyActivePromoPages } from 'src/app/core/types/promo.type';
import {
  buildDailyMission,
  checkActivatedTodayLootbox,
  getTodaySuccessfulDeposits,
  prepareBonusIds,
  replaceGoalAmountByCurrency,
  replaceStringInPageFields,
  resolveActivePromo,
  resolveBoxBonuses,
  resolveCurrentPromoBonus,
  resolvePromoParticipationState,
  resolveSlotLevelsBonuses,
} from '../../core/helpers/promo-utils';
import { ModalService } from '../../core/modal-v2/modal.service';
import { EnvironmentService } from '../../core/services/environment.service';
import { GlobalEventsService } from '../../core/services/global-events.service';
import { GroupsService } from '../../core/services/groups.service';
import { OffersService } from '../../core/services/offers.service';
import { UserBonusesService } from '../../core/services/user/user-bonuses.service';
import { UserTransactionsService } from '../../core/services/user/user-transactions.service';
import { UserService } from '../../core/services/user/user.service';
import { GlobalStoreName } from '../../core/store';
import { PromotionsStoreEntity } from '../../core/store/promotions.store';
import { CurrencyService } from 'src/app/core/services/currency.service';

@Injectable({
  providedIn: 'root',
})
export class PromotionResolverService {
  private _offers = inject(OffersService);
  private _router = inject(Router);
  private _user = inject(UserService);
  private _bonuses = inject(UserBonusesService);
  private _time = inject(TimeService);
  private _group = inject(GroupsService);
  private _cookie = inject(CookieService);
  private _events = inject(GlobalEventsService);
  private _platform = inject(PlatformService);
  private _modals = inject(ModalService);
  private _transactions = inject(UserTransactionsService);
  private _env = inject(EnvironmentService);
  private _currency = inject(CurrencyService);
  private _destroyRef = inject(DestroyRef);
  private _welcomeModalInitialized = false;

  private _promotionsStore = useStore<PromotionsStoreEntity>(GlobalStoreName.PROMOTIONS_STORE);

  get store(): PromotionsStoreEntity {
    return this._promotionsStore.storeSignal();
  }

  public resolvePromos() {
    this._user.auth$
      .pipe(
        switchMap(() => this._fetchPromoData()),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  private _fetchPromoData() {
    this.initWelcomePromoModal();
    return combineLatest([
      this._offers.list({ category_slug: `promotions-fiat` }).pipe(filter((list) => !!list?.length)),
      ...this._loadNecessaryBonuses$(),
    ]).pipe(
      map(([pageList, lootbox, fs, bonuses]) => {
        const activePromoPage = this._prepareActivePromoPage(resolveActivePromo(pageList), lootbox);
        const currentBonus = resolveCurrentPromoBonus(lootbox, fs, bonuses, activePromoPage.BonusesIds, this._cookie);
        const prizeImages = this._resolveShuffledPrizeImages(activePromoPage);
        const shouldResetSpinState = !!currentBonus;

        const activePromoPageWithDefaultGoalAmount = replaceGoalAmountByCurrency(activePromoPage, this._env.dataEnv()?.currency?.short, this._currency);

        this._patchPromotionsStore({
          activePromo: { ...this.store.activePromo, page: activePromoPageWithDefaultGoalAmount, currentBonus: currentBonus, prizeImages },
          allPromos: pageList,
          isLoaded: true,
          ...(shouldResetSpinState && { isSpinFinished: false }),
        });

        this._updateParticipateInPromo(lootbox, activePromoPage.BonusesIds);
        return activePromoPage;
      }),
      switchMap((page) => this._resolveMissionTasks(page)),
    );
  }

  private _resolveShuffledPrizeImages(activePromoPage: any) {
    const prizeImages = Object.entries(activePromoPage)
      .filter(([key]) => key.includes('PrizeImage'))
      .map(([, value]) => value);

    //shuffle images
    for (let i = prizeImages.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [prizeImages[i], prizeImages[j]] = [prizeImages[j], prizeImages[i]];
    }

    return prizeImages;
  }

  private _loadNecessaryBonuses$() {
    return [
      this._user.auth ? this._bonuses.lootBoxList$ : of([]),
      this._user.auth ? this._bonuses.freeSpinBonuses$ : of([]),
      this._user.auth ? this._bonuses.moneyBonuses$ : of([]),
    ];
  }

  private _updateParticipateInPromo(lootboxes: any, bonusIds: string) {
    const isUserAlreadyActivatedPromoLootbox = checkActivatedTodayLootbox(lootboxes, prepareBonusIds(bonusIds), this._time);
    const participationState = resolvePromoParticipationState(
      this.store.activePromo?.currentBonus,
      this._group,
      isUserAlreadyActivatedPromoLootbox,
    );

    this._patchPromotionsStore({
      activePromo: {
        ...this.store.activePromo,
        ...participationState,
      },
    });
  }

  private _prepareActivePromoPage(page: OnlyActivePromoPages | any, lootbox: any[]) {
    return {
      ...page,
      HowWorksSteps: [...Object.values(page?.HowWorksSteps || {})],
      WelcomeModal: Object.values(page?.WelcomePromoModal || page?.WelcomeModal || {})[0],
      Levels: resolveSlotLevelsBonuses([...Object.values(page?.Levels || {})], lootbox, prepareBonusIds(page?.BonusesIds), this._time),
      AmountGoalByCurrency: Object.values(page?.AmountGoalByCurrency || {})[0],
      AmountMidGoalByCurrency: Object.values(page?.AmountMidGoalByCurrency || {})[0],
      DailyMission: Object.values(page?.DailyMission || {})[0],
      BonusCards: resolveBoxBonuses([...Object.values(page?.BonusCards || {})], lootbox, prepareBonusIds(page?.BonusesIds), this._time),
      unpublishAt: this._time.toLocalDate(page?.unpublishAt),
      publishAt: this._time.toLocalDate(page?.publishAt?.date),
    };
  }

  private _resolveMissionTasks(page: any) {
    if (page) {
      return combineLatest([this._user.auth$, this._user.currency$]).pipe(
        filter(([auth]) => !!auth),
        switchMap(() => this._transactions.transactionList()),
        tap((list: any[]) => {
          const isTask1Completed = page?.Levels.some((level: any) => level.isTaken);
          const todaySuccessfulDeposits = getTodaySuccessfulDeposits(list, this._time);
          const lastSuccessDepCurrency = todaySuccessfulDeposits?.[0]?.currency || this._user.currentCurrency.currency || this._env.dataEnv()?.currency?.short;
          const lastSuccessDepCurrencySymbol = this._currency.currencySymbol(lastSuccessDepCurrency);
          const goalAmount = page?.AmountGoalByCurrency?.[lastSuccessDepCurrency];
          const midGoalAmount = page?.AmountMidGoalByCurrency?.[lastSuccessDepCurrency];
          const todaySuccessfulDepositAmount = todaySuccessfulDeposits.reduce((sum: number, transaction: any) => sum + (Number(transaction?.amount) || 0), 0);
          const dailyMission = buildDailyMission({
            mission: page?.DailyMission,
            isTask1Completed,
            todaySuccessfulDepositAmount,
            lastSuccessDepCurrency,
            lastSuccessDepCurrencySymbol,
            goalAmount,
            midGoalAmount,
          });
          const activePromoPage = {
            ...page,
            DailyMission: dailyMission,
          };

          this._patchPromotionsStore({
            activePromo: {
              ...this.store.activePromo,
              page: replaceStringInPageFields(activePromoPage, '#goalAmount#', `${lastSuccessDepCurrencySymbol}${Number(goalAmount)}`),
            },
          });
        }),
      );
    }
    return of(null);
  }

  public initWelcomePromoModal() {
    if (this._platform.isBrowser && !this._welcomeModalInitialized) {
      this._welcomeModalInitialized = true;
      this._checkShouldOpenWelcomeModal$().pipe(takeUntilDestroyed(this._destroyRef)).subscribe();
    }
  }

  private _checkShouldOpenWelcomeModal$(): Observable<boolean> {
    const routesIgnore = ['403', '404', 'loyalty', 'register', 'login', 'deposit', 'cashout', 'games', 'promo', 'play'];

    return this._events.routerNavigationEnd$.pipe(
      takeWhile(() => !this._cookie.check('welcome-modal-promo'), true),
      distinctUntilChanged(),
      delay(120000),
      filter(() => {
        const hasWelcomeModalCookie = this._cookie.check('welcome-modal-promo');
        const shouldOpenModal = this._user.auth && !hasWelcomeModalCookie;
        return !!this.store.activePromo?.page && shouldOpenModal && !routesIgnore.some((route) => this._router.url.includes(route));
      }),
      switchMap(() => this._checkActivatedBonuses()),
    );
  }

  private _checkActivatedBonuses(): Observable<boolean> {
    const { activePromo } = this.store;
    const isLoadedPromo = this.store.isLoaded && !!activePromo?.page;

    if (activePromo?.isWelcomeModalOpened || !isLoadedPromo || activePromo?.isUserAlreadyActivatedPromoLootbox || !activePromo.page.WelcomeModal) {
      return of(false);
    }

    return from(this._openWelcomeModal());
  }

  private async _openWelcomeModal(): Promise<boolean> {
    this._patchPromotionsStore({
      activePromo: {
        ...this.store.activePromo,
        isWelcomeModalOpened: true,
      },
    });

    const { WelcomePromoModalComponent } = await import('../../core/modal-v2/components/lazy/welcome-promo-modal/welcome-promo-modal.component');
    await this._modals.openLazy(WelcomePromoModalComponent, { template: 'CLEAR' });

    const endOfWeek = new Date();
    endOfWeek.setDate(endOfWeek.getDate() + (7 - endOfWeek.getDay()));
    endOfWeek.setHours(23, 59, 59, 999);

    this._cookie.set('welcome-modal-promo', 'true', endOfWeek, '/');

    return true;
  }

  private _patchPromotionsStore(patch: Partial<PromotionsStoreEntity>) {
    this._promotionsStore.storeSignal.update((store) => ({ ...store, ...patch }));
  }
}
